import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { TranslocoDatePipe } from '@ngneat/transloco-locale';
import { parseISO } from 'date-fns';
import { ComplexValue, Device, DeviceCategory, DeviceState, Media } from '../../model/device';

export interface DevicesState extends EntityState<Device> { }


const maxImageFeedLength = 10;


@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'Devices' })
export class DevicesStore extends EntityStore<DevicesState> {
  private sensorTimeouts = {};

  constructor(private datePipe: TranslocoDatePipe) {
    super();
    this.akitaPreAddEntity = this.akitaPreAddEntity.bind(this);
    this.akitaPreUpdateEntity = this.akitaPreUpdateEntity.bind(this);
  }

  akitaPreAddEntity(device: Device) {
    const deviceTimeout = this.getDeviceTimeout(device);

    // Start timeouts for all sensors upon first insertion.
    // Each sensor will be turned offline after this time.
    if (device.id.toString().includes('Sensor')) {
      this.sensorTimeouts[device.id] = setTimeout(
        () => this.setOffline(device),
        deviceTimeout
      );
    }

    // If a device hasn't reported in its update interval, mark as offline immediately
    if (parseISO(device.dateLastValueReported).getTime() < (Date.now() - deviceTimeout)) {
      device.deviceState = DeviceState.OFFLINE;
    }

    if (device.category.includes(DeviceCategory.MEDIA)) {
      const imageFeed = device.value != null
        ? [device.value] as Media[]
        : [];
      return {
        ...device, dateForDisplay: this.datePipe.transform(device.dateLastValueReported),
        imageFeed
      };
    }

    return {
      ...device,
      dateForDisplay: this.datePipe.transform(device.dateLastValueReported),
    };
  }

  akitaPreUpdateEntity(old: Device, updated: Device) {
    // Since this device just reported, reset its timeout.
    // Status change from the timeout will trigger update, so we need to filter it out by comparing report dates.
    if (this.sensorTimeouts[updated.id] != null && old.dateLastValueReported !== updated.dateLastValueReported) {
      clearTimeout(this.sensorTimeouts[updated.id]);
      this.sensorTimeouts[updated.id] = setTimeout(
        () => this.setOffline(updated),
        this.getDeviceTimeout(updated)
      );
    }

    updated.dateForDisplay = this.datePipe.transform(updated.dateLastValueReported);

    if (updated.category.includes(DeviceCategory.MEDIA) && (updated.value as ComplexValue).mediaType === 'IMAGE') {
      const imageFeed = [...old.imageFeed];

      const image = updated.value as Media;

      // Uncomment the following line to only add images that are not in the feed already.
      // This should happen when drones start sending actual data.

      imageFeed.unshift(image);
      if (imageFeed.length > maxImageFeedLength) { imageFeed.pop(); }
      return { ...updated, imageFeed };
    }

    return { ...updated };
  }

  private getDeviceTimeout(device: Device) {
    const defaultTimeout = 3600;
    const deviceTimeout = device.configuration?.reportingInterval;

    return (deviceTimeout ?? defaultTimeout) * 1000; // Convert to milliseconds since the value seems to be in seconds.
  }

  private setOffline(device: Device) {
    this.update(device.id, { deviceState: DeviceState.OFFLINE });
  }
}
